import React, { useState } from 'react'
import TransactionTable from 'transactions/table'
import DownloadTransactions from 'transactions/table/actions'
import ColumnSelect from 'dashboard/column-select'
import FilterGroups from 'dashboard/filter-groups'
import { Row, Col } from 'react-bootstrap'
import ScheduleEmailButton from 'dashboard/schedule-email-button'
import DeleteButton from 'dashboard/delete-button'
import { useDuplicateDashboard } from 'api/hooks/use-dashboards'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { useTableColumns } from 'api/hooks/use-table-columns'
import { transactionFilterParams, viewFilters } from 'api/transaction-query-params'
import { useQuery } from 'react-query'
import customColumnService from 'services/customColumnService'
import { useContext } from 'react'
import AuthContext from 'context/auth-context'
import { viewPath } from 'helpers/utils'
import { viewGridTableProps } from './view-grid'

function TopTransactions({
  setParams,
  params,
  sectionUnit,
  sectionName,
  dashboardId,
  dashboard,
  isFilterOpen,
  canEdit,
  isUserCreator,
  setDashboardId,
  referenceSearch = null,
  onReferenceSearchHandled = null
}) {
  const [showCleared] = useState(false)
  const [sessionTransactionListId, setSessionTransactionListId] = useState('')
  // While the column manager modal is open, pause the heavy transactions fetch.
  // Add/remove/reorder edits commit immediately and refresh the (cheap) dashboard
  // metadata, but the table data only re-runs once the modal closes — so editing
  // many columns one at a time no longer fires a transactions query per change.
  const [columnsModalOpen, setColumnsModalOpen] = useState(false)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const token = useContext(AuthContext)

  const { data: columns = [] } = useTableColumns({
    params: { section_id: params.section_id },
    options: {}
  })

  // Fetch custom columns for this dashboard
  const { data: customColumns = [] } = useQuery(
    ['customColumns', dashboardId],
    () => customColumnService.getCustomColumns(token, dashboardId),
    {
      enabled: !!dashboardId && !!token
    }
  )
  const { mutate: duplicateDashboard } = useDuplicateDashboard({
    id: dashboardId,
    options: {
      onSuccess: ({ id }) => {
        queryClient.invalidateQueries('dashboards')
        setDashboardId(id)
        navigate(viewPath(sectionName, id) || '/dashboards')
      }
    }
  })

  const { queryParams, tableProps } = viewGridTableProps({
    params,
    dashboard,
    columns,
    customColumns,
    transactionListId: sessionTransactionListId,
    showCleared
  })

  // The same params the grid requests, so an export holds exactly the rows on screen.
  const exportParams = {
    orderBy: queryParams.orderBy,
    orderDirection: queryParams.orderDirection,
    ...transactionFilterParams(
      { ...viewFilters(params), showCleared, transactionListId: sessionTransactionListId },
      { columns, customColumns }
    )
  }

  return (
    <div
      className="bg-white border border-1 rounded"
      style={{ borderColor: '#D2D2D2' }}
    >
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center p-2">
            <div className="fw-bold">{sectionUnit && `${sectionUnit}`}</div>
            <div className="d-flex">
              <div className="fw-bold me-2">
                <ScheduleEmailButton dashboard={dashboard} />
              </div>
              <div className="fw-bold me-2">
                <FilterGroups
                  params={params}
                  setParams={setParams}
                  columns={columns}
                  customColumns={customColumns}
                  canEdit={canEdit}
                  dashboardId={dashboardId}
                />
              </div>
              <div className="fw-bold">
                <ColumnSelect
                  disabled={!canEdit}
                  isUserCreator={isUserCreator}
                  params={params}
                  setParams={setParams}
                  sectionId={params.section_id}
                  dashboardId={dashboardId}
                  currentColumns={dashboard?.columns || []}
                  currentAnalytics={dashboard?.analytics_columns || []}
                  onOpenChange={setColumnsModalOpen}
                />
              </div>
              <div className="fw-bold me-2">
                <DownloadTransactions
                  defaultName={'Export'}
                  source="Views"
                  queryParams={exportParams}
                  onDuplicate={() => duplicateDashboard()}
                />
              </div>
              {isUserCreator && (
                <div className="fw-bold">
                  <DeleteButton dashboardId={dashboardId} />
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {queryParams.section_id.length > 0 && (
        <TransactionTable
          {...tableProps}
          isFilterOpen={isFilterOpen}
          columnModalOpen={columnsModalOpen}
          dashboardSetParams={setParams}
          source={`${sectionName} - ${sectionUnit}`}
          footerEnabled={true}
          title={''}
          dashboardId={dashboardId}
          canEdit={canEdit}
          referenceSearch={referenceSearch}
          onReferenceSearchHandled={onReferenceSearchHandled}
          onTransactionListIdChange={setSessionTransactionListId}
        />
      )}
    </div>
  )
}

export default TopTransactions
